(() => {
  const recentlyViewedKey = 'suncoViewedProducts';

  function parseJsonScript(script) {
    if (!script) return null;

    try {
      return JSON.parse(script.textContent || '{}');
    } catch (_error) {
      return null;
    }
  }

  function normalizeViewedProduct(product) {
    if (!product || typeof product !== 'object' || product.id == null) return null;

    const title = String(product.title || '').trim();
    const url = String(product.url || '').trim();

    if (!title || !url) return null;

    return {
      id: String(product.id),
      title,
      url,
      image: typeof product.image === 'string' ? product.image : product.image?.src || '',
      price: String(product.price || ''),
      card_html: product.card_html ? String(product.card_html) : '',
    };
  }

  function readRecentlyViewed() {
    try {
      const stored = JSON.parse(localStorage.getItem(recentlyViewedKey) || '[]');
      return Array.isArray(stored) ? stored.map(normalizeViewedProduct).filter(Boolean) : [];
    } catch (_error) {
      return [];
    }
  }

  function writeRecentlyViewed(products) {
    localStorage.setItem(recentlyViewedKey, JSON.stringify(products.map(normalizeViewedProduct).filter(Boolean).slice(0, 12)));
  }

  function recentlyViewedCardFragment(product, index) {
    if (!product.card_html) return null;

    const template = document.createElement('template');
    template.innerHTML = product.card_html;
    const fragment = template.content.cloneNode(true);
    const cards = fragment.querySelectorAll('[data-sunco-product-card]');

    if (!cards.length) return null;

    cards.forEach((card) => {
      card.dataset.suncoCardPosition = String(index + 1);
    });

    return fragment;
  }

  async function fetchRecentlyViewedCardHtml(product) {
    if (!product.url) return '';

    try {
      const response = await fetch(new URL(product.url, window.location.origin).toString(), {
        credentials: 'same-origin',
      });

      if (!response.ok) return '';

      const html = await response.text();
      const documentFragment = new DOMParser().parseFromString(html, 'text/html');
      const currentCardTemplate = documentFragment.querySelector('[data-sunco-recent-current-card-template]');

      return currentCardTemplate?.innerHTML || '';
    } catch (_error) {
      return '';
    }
  }

  function renderRecentlyViewedCards(section, grid, products, designMode) {
    grid.replaceChildren();

    products.forEach((product, index) => {
      const fragment = recentlyViewedCardFragment(product, index);
      if (fragment) grid.append(fragment);
    });

    if (grid.children.length === 0) {
      section.hidden = !designMode;
      return;
    }

    if (window.SuncoPlp?.setupProductCards) {
      window.SuncoPlp.setupProductCards(section);
    }
    window.SuncoProductRails?.init?.(section);

    section.hidden = false;
  }

  function setupRecentlyViewed(root = document) {
    root.querySelectorAll('[data-sunco-search-recently-viewed]').forEach((section) => {
      const data = parseJsonScript(section.querySelector('script[type="application/json"]')) || {};
      const grid = section.querySelector('[data-sunco-search-recent-grid]');
      if (!grid) return;

      const products = readRecentlyViewed().slice(0, data.limit || 8);
      grid.replaceChildren();

      if (products.length === 0) {
        section.hidden = !data.design_mode;
        return;
      }

      renderRecentlyViewedCards(section, grid, products, data.design_mode);

      if (products.every((product) => product.card_html)) return;

      Promise.all(
        products.map(async (product) => {
          if (product.card_html) return product;

          const cardHtml = await fetchRecentlyViewedCardHtml(product);
          return cardHtml ? { ...product, card_html: cardHtml } : product;
        })
      ).then((resolvedProducts) => {
        const resolvedById = new Map(resolvedProducts.map((product) => [String(product.id), product]));
        writeRecentlyViewed(
          readRecentlyViewed().map((product) => {
            const resolvedProduct = resolvedById.get(String(product.id));
            return resolvedProduct?.card_html ? { ...product, card_html: resolvedProduct.card_html } : product;
          })
        );
        renderRecentlyViewedCards(section, grid, resolvedProducts, data.design_mode);
      });
    });
  }

  document.addEventListener('DOMContentLoaded', () => setupRecentlyViewed());
  document.addEventListener('shopify:section:load', (event) => setupRecentlyViewed(event.target));
})();

(() => {
  const initializedSections = new WeakSet();
  const swatchBoxSelector = '#snize-search-results-list-mode .snize-color-swatch-box';
  const selectedColorGroupClass = 'sunco-snize-color-group';
  const textOnlyColorGroupClass = 'sunco-snize-color-group--text-only';
  const forcedVisibleColorGroupClass = 'sunco-snize-color-group--show-hidden-swatch';
  const optionsWithColorSwatchesClass = 'sunco-snize-options--has-color-swatches';

  function hasUsableSwatchVisual(swatch) {
    const visual = swatch?.querySelector('span');
    if (!visual) return false;

    const style = window.getComputedStyle(visual);
    const backgroundImage = style.backgroundImage || '';
    const hasImage =
      backgroundImage !== 'none' && !/(?:undefined|null)(?:["')]|$)/i.test(backgroundImage);
    const hasColor =
      style.backgroundColor !== 'transparent' &&
      style.backgroundColor !== 'rgba(0, 0, 0, 0)';

    return hasImage || hasColor;
  }

  function syncSelectedColorLabel(swatchBox) {
    if (!swatchBox) return;

    const optionsBox = swatchBox.closest('.snize-options-box');
    const activeSwatch = swatchBox.querySelector('.snize-color-swatch-active');
    const value = activeSwatch?.dataset.value || activeSwatch?.title || '';
    let group = swatchBox.parentElement?.classList.contains(selectedColorGroupClass)
      ? swatchBox.parentElement
      : null;
    let label =
      group?.querySelector(':scope > .sunco-snize-selected-option') ||
      optionsBox?.querySelector(':scope > .sunco-snize-selected-option');

    if (!optionsBox || !value) {
      label?.remove();
      optionsBox?.classList.remove(optionsWithColorSwatchesClass);
      return;
    }

    if (!group) {
      group = document.createElement('div');
      group.className = selectedColorGroupClass;
      swatchBox.insertAdjacentElement('beforebegin', group);
      group.append(swatchBox);
    }

    if (!label) {
      label = document.createElement('span');
      label.className = 'sunco-snize-selected-option';
      label.setAttribute('aria-hidden', 'true');
    }

    if (label.parentElement !== group) group.append(label);
    label.textContent = `Color: ${value}`;

    const swatches = Array.from(swatchBox.querySelectorAll('.snize-color-swatch'));
    const hasVisibleSwatch = swatches.some(
      (swatch) => window.getComputedStyle(swatch).display !== 'none'
    );
    const showHiddenSingleSwatch =
      !hasVisibleSwatch && swatches.length === 1 && hasUsableSwatchVisual(swatches[0]);
    const showSwatches = hasVisibleSwatch || showHiddenSingleSwatch;

    group.classList.toggle(textOnlyColorGroupClass, !showSwatches);
    group.classList.toggle(forcedVisibleColorGroupClass, showHiddenSingleSwatch);
    optionsBox.classList.toggle(optionsWithColorSwatchesClass, showSwatches);
  }

  function hydrateSelectedColorLabels(root) {
    if (root.matches?.(swatchBoxSelector)) syncSelectedColorLabel(root);
    root.querySelectorAll?.(swatchBoxSelector).forEach(syncSelectedColorLabel);
  }

  function setupSelectedColorLabels(root = document) {
    const sections = root.matches?.('[data-sunco-searchanise-results]')
      ? [root]
      : root.querySelectorAll?.('[data-sunco-searchanise-results]') || [];

    sections.forEach((section) => {
      if (initializedSections.has(section)) return;

      initializedSections.add(section);
      hydrateSelectedColorLabels(section);

      new MutationObserver((mutations) => {
        mutations.forEach((mutation) => {
          mutation.addedNodes.forEach((node) => {
            if (node.nodeType === Node.ELEMENT_NODE) hydrateSelectedColorLabels(node);
          });
        });
      }).observe(section, { childList: true, subtree: true });

      const syncAfterInteraction = (event) => {
        const swatch = event.target.closest?.('.snize-color-swatch');
        if (!swatch || !section.contains(swatch)) return;
        requestAnimationFrame(() => syncSelectedColorLabel(swatch.closest('.snize-color-swatch-box')));
      };

      section.addEventListener('click', syncAfterInteraction);
      section.addEventListener('keydown', (event) => {
        if (event.key === 'Enter' || event.key === ' ') syncAfterInteraction(event);
      });
    });
  }

  if (document.readyState === 'loading') {
    document.addEventListener('DOMContentLoaded', () => setupSelectedColorLabels());
  } else {
    setupSelectedColorLabels();
  }

  document.addEventListener('shopify:section:load', (event) => setupSelectedColorLabels(event.target));
})();
